import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Lazy load components for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const ContentCreator = lazy(() => import('./components/ContentCreator'));
const VideoEditor = lazy(() => import('./components/VideoEditor'));
const Scheduler = lazy(() => import('./components/Scheduler'));
const Analytics = lazy(() => import('./components/Analytics'));
const SocialIntegrations = lazy(() => import('./components/SocialIntegrations'));
const BrandTrainer = lazy(() => import('./components/BrandTrainer'));
const Settings = lazy(() => import('./components/Settings'));
const PaymentSuccess = lazy(() => import('./components/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./components/PaymentCancel'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const RefundPolicy = lazy(() => import('./components/RefundPolicy'));
const ChatBot = lazy(() => import('./components/ChatBot'));
const TeamCollaboration = lazy(() => import('./components/TeamCollaboration'));
const CreatorCRM = lazy(() => import('./components/CreatorCRM'));
const TemplateLibrary = lazy(() => import('./components/TemplateLibrary'));
const ValuesFilter = lazy(() => import('./components/ValuesFilter'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-black">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-500 mx-auto mb-4"></div>
      <p className="text-white text-lg">Loading...</p>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('omnipost_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('omnipost_user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('omnipost_user');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-blue-900 to-black">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-500"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route
            path="/"
            element={!user ? <LandingPage onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/how-it-works"
            element={<HowItWorks />}
          />
          <Route
            path="/payment/success"
            element={<PaymentSuccess />}
          />
          <Route
            path="/payment/cancel"
            element={<PaymentCancel />}
          />
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/create"
            element={user ? <ContentCreator user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/video-editor"
            element={user ? <VideoEditor user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/scheduler"
            element={user ? <Scheduler user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/analytics"
            element={user ? <Analytics user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/integrations"
            element={user ? <SocialIntegrations user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/brand"
            element={user ? <BrandTrainer user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/settings"
            element={user ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/team"
            element={user ? <TeamCollaboration user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/crm"
            element={user ? <CreatorCRM user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/templates"
            element={user ? <TemplateLibrary user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/values-filter"
            element={user ? <ValuesFilter user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/privacy-policy"
            element={<PrivacyPolicy />}
          />
          <Route
            path="/terms-of-service"
            element={<TermsOfService />}
          />
          <Route
            path="/refund-policy"
            element={<RefundPolicy />}
          />
        </Routes>
      </Suspense>
      
      {/* ChatBot with separate Suspense (PERFORMANCE FIX) */}
      <Suspense fallback={null}>
        <ChatBot />
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
